import React from 'react';
import { SportCarousel } from 'features/sport/components/sportCarousel';
import { SportTeamCard } from './components/sportTeamCard';
import { SportTopManifestazioniSkeleton } from 'features/sport/components/sportTopManifestazioni';
import { SwiperSlide } from 'swiper/react';
import { UsersUserDataSectionEnum } from 'types/swagger';
import { isTruthy } from 'utility/functions';
import { useGetFavouritesBySectionQuery } from 'features/api/apiSlice';
import { useGetTeamsQuery } from 'features/api/sportApiSlice';
import { useHideNavigationCarousel } from 'hooks';
import { useInternalSession } from 'hooks/useInternalSession';
import { useMemo } from 'react';
import { useRouter } from 'next/router';
import useWindowSize from 'hooks/useWindowSize';

export type TeamsCarouselProps = {
  title: string;
  className: string;
  slug: string;
  idDisciplina: number;
  idManifestazione: number;
};

export const TeamsCarousel = ({ slug, title, className, idDisciplina, idManifestazione }: TeamsCarouselProps) => {
  const { isReady } = useRouter();
  const {
    data: teams,
    isLoading,
    isFetching,
  } = useGetTeamsQuery(`${idDisciplina}-${idManifestazione}`, { skip: !isReady });

  let { isScrollable, swiperSlideRef } = useHideNavigationCarousel({ className: 'disabled', items: teams });
  const { isDesktop } = useWindowSize();

  const { isAuthenticated } = useInternalSession();

  const { data: response } = useGetFavouritesBySectionQuery(UsersUserDataSectionEnum.FAVORITE_SPORT_SQUADRA, {
    skip: !isAuthenticated,
  });

  const favouriteTeam = useMemo(() => {
    if (!response) return null;
    return teams?.find((team) => {
      return response?.[0]?.id === `${idDisciplina}-${idManifestazione}-${team.name}`;
    });
  }, [teams, response, idManifestazione, idDisciplina]);

  if (isLoading || isFetching) {
    return <SportTopManifestazioniSkeleton key={`TeamsCarousel-skeleton`} />;
  }

  if (!isTruthy(teams?.length)) {
    return <React.Fragment key={`TeamsCarousel-skeleton`} />;
  }

  return (
    <SportCarousel
      title={title}
      className={className}
      navigation={isScrollable}
      hasNoTopPadding={isDesktop}
      hasNoLeftPadding={isDesktop}
      onInit={(core) => (swiperSlideRef!.current = core.el)}
    >
      {teams?.map((item, index) => (
        <SwiperSlide key={index}>
          <SportTeamCard
            {...item}
            slug={slug}
            idDisciplina={idDisciplina}
            idManifestazione={idManifestazione}
            favouriteTeam={favouriteTeam}
          />
        </SwiperSlide>
      ))}
    </SportCarousel>
  );
};
